import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Home, ChevronRight, Users, Briefcase, MapPin, Landmark,
  AlertTriangle, Banknote, ChevronDown, Stethoscope,
} from 'lucide-react';
import { getLoanSubscriberDetail } from '../services/loanSubscribersService';
import { useAuth } from '../auth/AuthContext';

// ── Role helper ───────────────────────────────────────────────────────────────

const getCurrentRole = () => {
  const authRole = localStorage.getItem('authRole');
  return { wallet_superadmin: 'SuperAdmin', wallet_finance: 'Finance', wallet_ops: 'Ops', wallet_support: 'Support' }[authRole] || null;
};

// ── Formatters ────────────────────────────────────────────────────────────────

const fmt = (v) => (v != null && v !== '') ? String(v) : null;

const fmtAmount = (v) => {
  const n = Number(v);
  if (isNaN(n) || v == null || v === '') return null;
  return `₹${n.toLocaleString('en-IN')}`;
};

const fmtDate = (s) => {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d)) return null;
    return `${d.getDate()} ${d.toLocaleString('en-IN', { month: 'short' })} ${d.getFullYear()}`;
  } catch { return null; }
};

const fmtDateTime = (s) => {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d)) return null;
    const h = d.getHours(), m = String(d.getMinutes()).padStart(2, '0');
    return `${d.getDate()} ${d.toLocaleString('en-IN', { month: 'short' })} ${d.getFullYear()}, ${h % 12 || 12}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
  } catch { return null; }
};

const humanize = (s) => s ? s.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;

const EMP_MAP = { salaried: 'Salaried', self_employed: 'Self Employed', s: 'Salaried', e: 'Self Employed', p: 'Pensioner', u: 'Unemployed' };
const ACC_MAP = { saving: 'Saving', current: 'Current', s: 'Saving', c: 'Current' };
const OWN_MAP = { owned: 'Owned', rented: 'Rented', family: 'Family', company: 'Company', o: 'Owned', r: 'Rented', f: 'Family' };

// ── Data normaliser — handles both API shapes ─────────────────────────────────
// Shape A (DnD branch): { member, patient, employment_detail, addresses:{resident,permanent,company}, lenders, hospital, ... }
// Shape B (our API):    { personal: { full_name, mobile, ... }, lender_attempts, family_members, ... }

const normalise = (raw) => {
  if (!raw) return null;

  // Shape B — has a `personal` wrapper
  if (raw.personal) {
    const p = raw.personal;
    return {
      // top-level loan fields
      id:                    raw.id,
      ref_code:              raw.ref_code,
      status:                raw.status,
      journey_type:          raw.journey_type,
      loan_amount_required:  raw.loan_amount_required,
      purpose_of_loan:       raw.purpose_of_loan,
      disease_type:          raw.disease_type || p.disease_type,
      plan_treatment_schedule: raw.plan_treatment_schedule,
      start_date:            raw.start_date,
      source:                raw.source,
      created_at:            raw.created_at,
      eligible_lenders:      raw.eligible_lenders,
      applied_lenders:       raw.applied_lenders,

      // member = borrower
      member: {
        name:           p.full_name,
        mobile:         p.mobile,
        email:          p.email,
        gender:         p.gender,
        dob:            p.date_of_birth,
        pan_number:     p.pan_number,
        aadhar_number:  p.aadhar_number,
        marital_status: p.marital_status,
        relation:       'self',
      },

      // patient — only when borrower is not the patient
      patient: p.is_patient === 'no' ? {
        name:     [p.patient_first_name, p.patient_last_name].filter(Boolean).join(' '),
        relation: p.relation_with_borrower,
        disease:  p.disease_type,
      } : null,

      // employment
      employment_detail: (p.company_name || p.monthly_income != null) ? {
        company_name:      p.company_name,
        employment_type:   p.employment_type,
        monthly_income:    p.monthly_income,
        work_experience:   p.work_experience,
        medium_of_salary:  p.medium_of_salary,
      } : null,

      // addresses
      addresses: (p.resident_address || p.permanent_address || p.company_address) ? {
        resident:  p.resident_address  || null,
        permanent: p.permanent_address || null,
        company:   p.company_address   || null,
      } : null,

      // bank — mapped to hospital slot so right column works uniformly
      bank_details: p.bank_details || null,

      // family
      family_members: raw.family_members || [],

      // lenders — normalise nested lender object
      lenders: (raw.lender_attempts || []).map(la => ({
        id:                      la.id,
        display_name:            la.lender_display_name || la.lender_name || (la.lender && la.lender.display_name),
        code:                    la.lender_code || (la.lender && la.lender.code),
        status:                  la.status,
        is_current:              la.is_current,
        loan_amount:             la.loan_amount,
        external_application_id: la.external_application_id,
        sanctioned_at:           la.sanctioned_at,
        disbursed_at:            la.disbursed_at,
        failure_reason:          la.failure_reason,
      })),
    };
  }

  // Shape A — DnD branch shape, already normalised
  return {
    ...raw,
    bank_details: null,
    family_members: raw.family_members || [],
    lenders: (raw.lenders || []).map(la => ({
      id:                      la.id,
      display_name:            la.lender?.display_name || la.lender?.name,
      code:                    la.lender?.code,
      status:                  la.status,
      is_current:              la.is_current,
      loan_amount:             la.loan_amount,
      external_application_id: la.external_application_id,
      sanctioned_at:           la.sanctioned_at,
      disbursed_at:            la.disbursed_at,
      failure_reason:          la.failure_reason,
    })),
  };
};

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_CFG = {
  'loan-availed':            { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500'    },
  'loan-disbursed':          { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'lender-rejected':         { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500'     },
  'eligibility-failed':      { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500'     },
  'loan-eligibility-passed': { bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500'    },
  'loan-application-started':{ bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-400'    },
  'offers-received':         { bg: 'bg-indigo-50',  text: 'text-indigo-700',  dot: 'bg-indigo-500'  },
  'kyc-completed':           { bg: 'bg-cyan-50',    text: 'text-cyan-700',    dot: 'bg-cyan-500'    },
};
const DEFAULT_STATUS_CFG = { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CFG[(status || '').toLowerCase()] || DEFAULT_STATUS_CFG;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ring-black/5 ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {humanize(status) || '—'}
    </span>
  );
};

// ── KV primitive ──────────────────────────────────────────────────────────────

const KV = ({ label, value, mono = false, highlight = false }) => (
  <div className="flex flex-col gap-0.5 min-w-0">
    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest truncate">{label}</span>
    <span className={[
      'text-sm leading-snug break-words',
      highlight ? 'text-emerald-600 font-bold' : 'text-gray-800',
      mono ? 'font-mono text-xs' : '',
      !value ? 'italic text-gray-300' : '',
    ].filter(Boolean).join(' ')}>
      {value || '—'}
    </span>
  </div>
);

// ── Empty state ───────────────────────────────────────────────────────────────

const Empty = ({ label = 'No information available' }) => (
  <p className="text-xs text-gray-400 italic py-1">{label}</p>
);

// ── Collapsible card ──────────────────────────────────────────────────────────

const Card = ({ title, icon: Icon, iconBg = 'bg-blue-50', iconColor = 'text-blue-600', count, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const tintBg = iconBg.replace('bg-', 'bg-').replace('-50', '-50/60');
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center gap-2.5 px-4 py-3 text-left transition-colors hover:bg-gray-50/60 ${tintBg} ${open ? 'border-b border-gray-100' : ''}`}
      >
        <div className={`w-7 h-7 rounded-xl ${iconBg} ${iconColor} flex items-center justify-center shrink-0`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-sm font-semibold text-gray-900 flex-1 truncate">
          {title}{count != null ? ` (${count})` : ''}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
};

// ── Address block ─────────────────────────────────────────────────────────────

const ADDR_ACCENT = {
  resident:  { dot: 'bg-cyan-400',   badge: 'bg-cyan-50 text-cyan-700',     label: 'Resident'  },
  permanent: { dot: 'bg-violet-400', badge: 'bg-violet-50 text-violet-700', label: 'Permanent' },
  company:   { dot: 'bg-orange-400', badge: 'bg-orange-50 text-orange-700', label: 'Company'   },
};

const AddressBlock = ({ type, addr }) => {
  if (!addr) return null;
  const acc = ADDR_ACCENT[type] || ADDR_ACCENT.resident;
  const line = [addr.address_line_1, addr.address_line_2].filter(Boolean).join(', ');
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-3 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${acc.dot}`} />
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${acc.badge}`}>{acc.label}</span>
        {addr.ownership_type && (
          <span className="ml-auto text-[10px] text-gray-400">{OWN_MAP[addr.ownership_type] || humanize(addr.ownership_type)}</span>
        )}
      </div>
      {line ? (
        <div className="flex gap-1.5">
          <MapPin className="w-3 h-3 text-gray-400 mt-0.5 shrink-0" />
          <p className="text-xs text-gray-600 leading-snug">{line}</p>
        </div>
      ) : null}
      {addr.landmark ? <p className="text-[10px] text-gray-400 pl-4">{addr.landmark}</p> : null}
      <div className="flex flex-wrap gap-2 text-[10px] text-gray-500">
        {addr.city    && <span>{addr.city}</span>}
        {addr.state   && <span>· {addr.state}</span>}
        {addr.pincode && <span className="font-mono bg-white border border-gray-200 px-1 rounded">{addr.pincode}</span>}
      </div>
    </div>
  );
};

// ── Lender card ───────────────────────────────────────────────────────────────

const LENDER_STATUS_CFG = {
  pending:              { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400'   },
  approved:             { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  success:              { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'loan-availed':       { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500'    },
  'loan-disbursed':     { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'lender-rejected':    { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500'     },
  rejected:             { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500'     },
  'eligibility-failed': { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500'     },
};

const LenderCard = ({ la }) => {
  const cfg = LENDER_STATUS_CFG[(la.status || '').toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
  const initial = (la.display_name || 'L').charAt(0).toUpperCase();
  return (
    <div className="rounded-xl bg-indigo-50/40 border border-indigo-100 px-4 py-3 space-y-3">
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center shrink-0 text-sm">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate">{la.display_name || '—'}</p>
          {la.code && <p className="text-[10px] text-indigo-500 font-medium">{la.code}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {la.is_current && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">
              ✓ Current
            </span>
          )}
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {humanize(la.status) || '—'}
          </span>
        </div>
      </div>
      <div className="border-t border-indigo-100" />
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <KV label="Loan Amount"   value={fmtAmount(la.loan_amount)} highlight />
        <KV label="Ext. App ID"   value={fmt(la.external_application_id)} mono />
        <KV label="Sanctioned At" value={fmtDateTime(la.sanctioned_at)} />
        <KV label="Disbursed At"  value={fmtDateTime(la.disbursed_at)} />
      </div>
      {la.failure_reason && (
        <div className="pt-2 border-t border-indigo-100 flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />
          <p className="text-xs text-red-500">{la.failure_reason}</p>
        </div>
      )}
    </div>
  );
};

// ── Skeleton ──────────────────────────────────────────────────────────────────

const Skeleton = () => (
  <div className="bg-white px-4 sm:px-8 py-5 max-w-7xl mx-auto space-y-4 animate-pulse">
    <div className="h-5 bg-gray-200 rounded w-72" />
    <div className="h-32 bg-gradient-to-br from-blue-200 to-teal-200 rounded-2xl" />
    <div className="flex flex-col xl:flex-row gap-4">
      <div className="w-full xl:w-80 space-y-3">
        {[80, 60, 96].map(h => (
          <div key={h} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="h-12 bg-gray-50" />
            <div className="p-4" style={{ height: h }}>
              <div className="h-3 bg-gray-100 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex-1 space-y-3">
        {[72, 88, 120].map(h => (
          <div key={h} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="h-12 bg-gray-50" />
            <div className="p-4" style={{ height: h }}>
              <div className="h-3 bg-gray-100 rounded w-2/3 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Breadcrumb ────────────────────────────────────────────────────────────────

const Breadcrumb = ({ onBack, name }) => (
  <div className="flex flex-col gap-1.5">
    <nav className="flex items-center gap-1.5 text-sm text-gray-400">
      <button onClick={() => window.location.assign('/subscriber-wallet')} className="flex items-center gap-1 hover:text-gray-600 transition-colors">
        <Home className="w-3.5 h-3.5" /><span>Home</span>
      </button>
      <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
      <button onClick={onBack} className="hover:text-gray-600 transition-colors">Loan Subscribers</button>
      <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
      <span className="text-gray-600 font-medium truncate max-w-[220px]">{name || '—'}</span>
    </nav>
    {/* <h1 className="text-2xl font-bold text-gray-900">{name || 'Subscriber Detail'}</h1> */}
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────

const LoanSubscriberDetail = () => {
  const { application_id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const currentRole = getCurrentRole();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (authLoading || !isAuthenticated || !currentRole || !application_id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getLoanSubscriberDetail(currentRole, application_id);
      if (!res?.metadata?.api_status) throw new Error('API returned an error status');
      setDetail(normalise(res.msg));
    } catch (err) {
      setError(err.message || 'Failed to load subscriber detail');
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, currentRole, application_id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (loading) return <Skeleton />;

  if (error) return (
    <div className="bg-white p-6">
      <button onClick={() => navigate('/subscriber-wallet/loan-subscribers')}
        className="text-sm text-gray-600 hover:text-gray-900 mb-6 flex items-center gap-1">
        ← Back
      </button>
      <div className="max-w-sm mx-auto mt-16 bg-red-50 border border-red-200 rounded-2xl px-8 py-10 text-center">
        <p className="text-red-600 text-sm mb-3">{error}</p>
        <button onClick={fetchDetail} className="text-xs text-red-700 underline">Retry</button>
      </div>
    </div>
  );

  if (!detail) return null;

  const d = detail;
  const m = d.member;
  const emp = d.employment_detail;
  const addr = d.addresses;
  const bank = d.bank_details;
  const lenders = d.lenders || [];
  const family = d.family_members || [];
  const eligibleLenders = Array.isArray(d.eligible_lenders) ? d.eligible_lenders.join(', ') : fmt(d.eligible_lenders);

  return (
    <div className="bg-white px-2 sm:px-3 py-5 max-w-[1800px] mx-[200px]">

      <div className="mb-6">
        <Breadcrumb onBack={() => navigate('/subscriber-wallet/loan-subscribers')} name={m?.name} />
      </div>

      {/* ── Two-column layout — w-80 sticky left + flex-1 right ── */}
      <div className="bg-gray-50 rounded-2xl p-4 sm:p-6 min-h-[calc(100vh-160px)]">
      <div className="flex flex-col xl:flex-row gap-4 items-start">

        {/* LEFT — fixed w-80, sticky */}
        <div className="w-full xl:w-80 xl:shrink-0 xl:sticky xl:top-6 flex flex-col gap-3">

          {/* Hero / Loan Overview */}
          <div className="rounded-2xl bg-gradient-to-br from-blue-700 via-blue-600 to-teal-500 px-4 py-4 relative overflow-hidden">
            <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/5 pointer-events-none" />
            <div className="relative z-10">
              <p className="text-[10px] font-semibold text-blue-200 uppercase tracking-widest mb-1">Loan Amount Required</p>
              <p className="text-2xl font-bold text-white leading-none mb-2 drop-shadow">{fmtAmount(d.loan_amount_required) || '—'}</p>
              <StatusBadge status={d.status} />
              <div className="mt-3 flex flex-col gap-1">
                {d.ref_code && <span className="font-mono text-[10px] font-semibold text-white/60 bg-white/10 px-2 py-0.5 rounded-md self-start">{d.ref_code}</span>}
                {fmtDate(d.created_at) && <span className="text-[10px] text-blue-200">Created {fmtDate(d.created_at)}</span>}
                {m?.name && <span className="text-xs font-semibold text-white/90 mt-0.5">{m.name}</span>}
              </div>
            </div>
            <div className="relative z-10 mt-3 grid grid-cols-2 gap-2">
              {[
                { label: 'Journey',    value: humanize(d.journey_type) },
                { label: 'Schedule',   value: humanize(d.plan_treatment_schedule) },
                { label: 'Start Date', value: fmtDate(d.start_date) },
                { label: 'Eligible',   value: eligibleLenders },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white/10 rounded-xl px-2.5 py-2">
                  <p className="text-[10px] text-blue-200/70 uppercase tracking-widest">{label}</p>
                  <p className="text-xs font-medium text-white/90 mt-0.5 truncate">{value || '—'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Employment */}
          <Card title="Employment" icon={Briefcase} iconBg="bg-amber-50" iconColor="text-amber-500">
            {!emp ? <Empty label="No employment information available" /> : (
              <div className="rounded-xl bg-amber-50/60 border border-amber-100 px-3 py-3 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-900 truncate">{fmt(emp.company_name) || '—'}</p>
                    <p className="text-[10px] text-amber-600 font-medium mt-0.5">
                      {EMP_MAP[emp.employment_type] || humanize(emp.employment_type) || '—'}
                    </p>
                  </div>
                  {emp.monthly_income != null && (
                    <div className="text-right shrink-0">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">Income</p>
                      <p className="text-sm font-bold text-emerald-600">{fmtAmount(emp.monthly_income)}</p>
                    </div>
                  )}
                </div>
                <div className="border-t border-amber-100" />
                <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                  <KV label="Experience" value={emp.work_experience != null ? `${emp.work_experience} yrs` : null} />
                  <KV label="Salary via" value={humanize(emp.medium_of_salary)} />
                </div>
              </div>
            )}
          </Card>

          {/* Addresses */}
          <Card title="Addresses" icon={MapPin} iconBg="bg-cyan-50" iconColor="text-cyan-500">
            {!addr ? <Empty label="No address information available" /> : (
              <div className="space-y-2">
                <AddressBlock type="resident"  addr={addr.resident} />
                <AddressBlock type="permanent" addr={addr.permanent} />
                <AddressBlock type="company"   addr={addr.company} />
                {!addr.resident && !addr.permanent && !addr.company && (
                  <Empty label="No addresses recorded" />
                )}
              </div>
            )}
          </Card>

        </div>{/* end LEFT */}

        {/* RIGHT — flex-1, wider cards */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">

          {/* Subscriber / Member */}
          <Card title="Subscriber" icon={Users} iconBg="bg-teal-50" iconColor="text-teal-600">
            {!m ? <Empty label="No subscriber information available" /> : (
              <div className="space-y-3">
                <div className="rounded-xl bg-teal-50/60 border border-teal-100 px-4 py-3 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{fmt(m.name) || '—'}</p>
                      {fmt(m.mobile) && <p className="text-[10px] text-teal-600 font-medium mt-0.5">{m.mobile}</p>}
                      {fmt(m.email) && <p className="text-[10px] text-gray-400 truncate">{m.email}</p>}
                    </div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-teal-600 text-white uppercase shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-200 inline-block" />Borrower
                    </span>
                  </div>
                  <div className="border-t border-teal-100" />
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
                    <KV label="Gender"        value={humanize(m.gender)} />
                    <KV label="Date of Birth" value={fmtDate(m.dob)} />
                    <KV label="Marital"       value={humanize(m.marital_status)} />
                    <KV label="PAN"           value={fmt(m.pan_number)} mono />
                    <div className="col-span-2 lg:col-span-2">
                      <KV label="Aadhar" value={fmt(m.aadhar_number)} mono />
                    </div>
                  </div>
                </div>

                {d.patient && (
                  <div className="rounded-xl bg-purple-50/60 border border-purple-100 px-4 py-3 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{fmt(d.patient.name) || '—'}</p>
                        {d.patient.disease && <p className="text-[10px] text-purple-600 font-medium mt-0.5">{humanize(d.patient.disease)}</p>}
                      </div>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-purple-600 text-white uppercase shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-200 inline-block" />Patient
                      </span>
                    </div>
                    <div className="border-t border-purple-100" />
                    <KV label="Relation with Borrower" value={humanize(d.patient.relation)} />
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Loan Purpose */}
          {(d.purpose_of_loan || d.disease_type || d.plan_treatment_schedule) && (
            <Card title="Loan Purpose" icon={Stethoscope} iconBg="bg-rose-50" iconColor="text-rose-500">
              <div className="rounded-xl bg-gradient-to-br from-slate-50 to-blue-50/40 border border-blue-100 overflow-hidden">
                <div className="flex items-start justify-between gap-4 px-4 pt-4 pb-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-1">Purpose of Loan</p>
                    <p className="text-base font-bold text-gray-900 leading-snug">{fmt(d.purpose_of_loan) || '—'}</p>
                  </div>
                  {d.plan_treatment_schedule && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold shrink-0 mt-1">
                      {d.plan_treatment_schedule}
                    </span>
                  )}
                </div>
                <div className="border-t border-blue-100 grid grid-cols-2 divide-x divide-blue-100">
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Condition</p>
                    <p className="text-sm font-semibold text-gray-800">{humanize(d.disease_type) || '—'}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Journey</p>
                    <p className="text-sm font-semibold text-gray-800 capitalize">{humanize(d.journey_type) || '—'}</p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Bank Details */}
          <Card title="Bank Details" icon={Landmark} iconBg="bg-indigo-50" iconColor="text-indigo-600">
            {!bank ? <Empty label="No bank details available" /> : (
              <div className="rounded-xl bg-indigo-50/40 border border-indigo-100 px-4 py-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{fmt(bank.bank_name) || '—'}</p>
                    {fmt(bank.branch_name) && <p className="text-[10px] text-indigo-500 font-medium mt-0.5">{bank.branch_name}</p>}
                  </div>
                  {bank.account_type && (
                    <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                      {ACC_MAP[bank.account_type] || humanize(bank.account_type)}
                    </span>
                  )}
                </div>
                <div className="border-t border-indigo-100" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
                  <KV label="Account Name"   value={fmt(bank.account_name)} />
                  <KV label="Account Number" value={fmt(bank.account_number)} mono />
                  <KV label="IFSC Code"      value={fmt(bank.ifsc_code)} mono />
                  <KV label="Branch Address" value={fmt(bank.branch_address)} />
                </div>
              </div>
            )}
          </Card>

          {/* Family Members */}
          {family.length > 0 && (
            <Card title="Family Members" icon={Users} iconBg="bg-teal-50" iconColor="text-teal-600" count={family.length}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {family.map((fm, idx) => (
                  <div key={fm.id || idx} className="rounded-xl bg-gray-50/80 border border-gray-100 px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900">{fmt(fm.name) || '—'}</p>
                      <span className="text-[10px] text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                        {humanize(fm.relation) || '—'}
                      </span>
                    </div>
                    <div className="border-t border-gray-100" />
                    <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                      <KV label="Age"     value={fm.age != null ? `${fm.age} yrs` : null} />
                      <KV label="DOB"     value={fmtDate(fm.dob)} />
                      <KV label="Gender"  value={humanize(fm.gender)} />
                      <KV label="Contact" value={fmt(fm.contact_number)} mono />
                      <div className="col-span-2"><KV label="Email" value={fmt(fm.email)} /></div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Lender Attempts */}
          <Card title="Lender Attempts" icon={Banknote} iconBg="bg-indigo-50" iconColor="text-indigo-600" count={lenders.length > 0 ? lenders.length : null}>
            {lenders.length === 0 ? (
              <Empty label="No lender attempts yet" />
            ) : (
              <div className="space-y-3">
                {lenders.map((la, idx) => <LenderCard key={la.id || idx} la={la} />)}
              </div>
            )}
          </Card>

        </div>{/* end RIGHT */}

      </div>
      </div>{/* end bg-gray-50 wrapper */}
    </div>
  );
};

export default LoanSubscriberDetail;
